// ─────────────────────────────────────────────────────────────────────────────
// CareersPage, the hiring page for a company that is not always hiring.
//
// The honest version of this page is small. It says how the team works, what
// the work actually feels like when the output is somebody's cash position,
// and what we look for. It does not list roles, salary bands, benefits or
// offices, because inventing any of those is a promise made to a stranger.
// When a role opens it gets posted here; until then the page offers one
// expression-of-interest block and nothing else.
//
// No customer, shop or vertical is named anywhere on it. Layout follows the
// shared kit: left-aligned headings that end in a period, alternating white and
// grey sections, and grey captioned <MediaSlot> stand-ins for photography that
// does not exist yet.
// ─────────────────────────────────────────────────────────────────────────────
import { Link } from 'react-router-dom';
import {
    BTN_PRIMARY,
    BTN_SECONDARY,
    Icon,
    Container,
    Section,
    Lead,
    Body,
    CardTitle,
    Key,
    MediaSlot,
    SectionHead,
    FeatureRow,
    FeatureGrid,
    CTASection,
    PageHero,
    MarketingShell,
    Seo,
} from '../../components/marketing/MarketingKit';
import { Reveal } from '../../components/marketing/Motion';

const CAREERS_EMAIL = 'careers@wavelengtherp.com';

// ── How the team operates ────────────────────────────────────────────────────
const HOW = [
    {
        icon: 'users',
        title: 'Small team, high trust',
        body: 'There are no layers of sign-off between noticing something is wrong and putting the fix in front of an operator.',
    },
    {
        icon: 'bolt',
        title: 'Ship small and often',
        body: 'Work goes out in small pieces most weeks. Nobody waits a quarter to find out whether the thing they built is used.',
    },
    {
        icon: 'pin',
        title: 'Remote, with overlap',
        body: 'People work where they think best and keep a few hours in common, so a decision does not take a day to make.',
    },
    {
        icon: 'book',
        title: 'Write it down',
        body: 'Decisions live in writing. Somebody who was not in the conversation should still be able to follow what was decided and why.',
    },
    {
        icon: 'shield',
        title: 'Money-grade rigor',
        body: "The output is somebody else's cash position. We hold the work to the standard we ask an accountant to trust.",
    },
    {
        icon: 'search',
        title: 'Close to the counter',
        body: 'You will hear how a store actually closes its day from the person doing it, not from a summary of a summary.',
    },
];

// ── What we look for ─────────────────────────────────────────────────────────
const LOOK_FOR = [
    {
        title: 'Care for the detail that costs money',
        body: 'Being off by a cent is a bug here, not a rounding preference. People who enjoy chasing that down are rare and very welcome.',
    },
    {
        title: 'Comfort in a trade that is not yours',
        body: 'You will learn how a counter weighs, prices, counts and banks before you write the screen for it. Curiosity about the trade matters more than knowing it already.',
    },
    {
        title: 'A taste for finishing',
        body: 'The last tenth of a feature is where the trust is: the empty state, the failed sync, the number that should read N/A instead of zero.',
    },
    {
        title: 'Plain writing',
        body: 'Explaining a messy problem clearly, in a paragraph somebody can act on, is most of the job on any team here.',
    },
];

export default function CareersPage() {
    return (
        <MarketingShell>
            <Seo
                title="Careers: building back office software next to the counter"
                description="How the Wavelength team works, what it is like to build back office software for multi-location operators, and how to get in touch when no role is posted."
                path="/careers"
                keywords="wavelength careers, back office software jobs, retail software engineering, remote product engineering, reconciliation software team"
                jsonLd={{
                    '@context': 'https://schema.org',
                    '@graph': [
                        {
                            '@type': 'WebPage',
                            '@id': 'https://wavelengtherp.com/careers#page',
                            url: 'https://wavelengtherp.com/careers',
                            name: 'Careers at Wavelength',
                            description:
                                'How the Wavelength team works and how to get in touch about future roles.',
                            publisher: { '@id': 'https://wavelengtherp.com/#organization' },
                        },
                        {
                            '@type': 'BreadcrumbList',
                            itemListElement: [
                                { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://wavelengtherp.com/' },
                                { '@type': 'ListItem', position: 2, name: 'Careers', item: 'https://wavelengtherp.com/careers' },
                            ],
                        },
                    ],
                }}
            />

            {/* ── Hero ───────────────────────────────────────────────────── */}
            <PageHero
                eyebrow="Careers"
                title="A small team, working close to the counter."
                lead={<>We build the back office for operators running more than one location. <Key>There is nothing posted today.</Key> When a role opens it is listed on this page, and until then a short note about your work is enough to start a conversation.</>}
                primary={{ label: 'Introduce yourself', to: '/contact' }}
                secondary={{ label: 'See the platform', to: '/features' }}
            >
                <MediaSlot
                    kind="Photo"
                    label="A desk in the middle of the working day, one laptop, one notebook, one printed statement"
                    note="Daylight, no faces, nothing staged. The screen shows a day being closed."
                    ratio="16 / 9"
                    className="!rounded-[28px]"
                />
            </PageHero>

            {/* ── How the team works ─────────────────────────────────────── */}
            <Section tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            eyebrow="How we work"
                            title="How the team works."
                            lead="Deliberately small, deliberately close to real trading days, and deliberately careful about the details an owner cannot afford to have wrong."
                            maxWidth="max-w-[540px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <FeatureGrid className="mt-12" items={HOW} cols={3} />
                    </Reveal>
                </Container>
            </Section>

            {/* ── What the work is like ──────────────────────────────────── */}
            <Section pad="lg">
                <Container>
                    <Reveal>
                        <FeatureRow
                            eyebrow="The work"
                            title="Building for operators is a different job."
                            body="The person on the other end of this software is standing at a safe at eleven at night trying to work out where four hundred dollars went. That changes what good looks like."
                            bullets={[
                                'The failure mode is a wrong number, not a wrong pixel',
                                'You will read drawer counts, settlement batches and bank statements until they make sense',
                                'What you build reaches a real store the same week it lands',
                                'The surface is wide: the web app, the iPad apps, the data behind them and the reconciliation itself',
                            ]}
                            cta={<Link to="/how-it-works" className={BTN_SECONDARY}>See how the product works<Icon name="arrow" className="h-4 w-4" /></Link>}
                            media={
                                <MediaSlot
                                    kind="Photo"
                                    label="A drawer count sheet, a printed bank statement and a laptop on one table"
                                    note="Overhead, daylight, no people in frame. The paper and the screen showing the same day."
                                    ratio="4 / 3"
                                    className="!rounded-[28px]"
                                />
                            }
                        />
                    </Reveal>
                </Container>
            </Section>

            {/* ── What we look for ───────────────────────────────────────── */}
            <Section tone="paper2" pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="What we look for."
                            lead="The same four things whatever the role turns out to be, whether it is engineering, data or working directly with operators."
                            maxWidth="max-w-[520px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <FeatureGrid className="mt-12" items={LOOK_FOR} cols={2} numbered />
                    </Reveal>
                </Container>
            </Section>

            {/* ── Open roles ─────────────────────────────────────────────── */}
            <Section pad="lg">
                <Container>
                    <Reveal>
                        <SectionHead
                            title="Open roles."
                            lead="Nothing is open at the moment. We would rather show an empty page than a list we are not really hiring against, so this section stays honest and gets updated the day that changes."
                            maxWidth="max-w-[560px]"
                        />
                    </Reveal>
                    <Reveal delay={80}>
                        <div className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px]">
                            <div className="flex flex-col gap-4 rounded-[24px] border border-[rgba(10,10,10,0.10)] bg-white p-7 sm:p-8">
                                <CardTitle>Expression of interest</CardTitle>
                                <Body>
                                    Tell us what you would want to own and one thing you have built that you are still pleased with. Say where you are and how you like to work. If it lines up with what comes next, we will write back, and if it does not, we will say so rather than leave you waiting.
                                </Body>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    <a href={`mailto:${CAREERS_EMAIL}`} className={BTN_PRIMARY}>
                                        Email {CAREERS_EMAIL}
                                        <Icon name="arrow" className="h-4 w-4" />
                                    </a>
                                    <Link to="/contact" className={BTN_SECONDARY}>Talk to us</Link>
                                </div>
                            </div>
                            <div className="flex flex-col justify-center gap-3 rounded-[24px] bg-[#F5F5F5] p-7 sm:p-8">
                                <CardTitle>When a role opens</CardTitle>
                                <Body>
                                    It is posted here with what the work involves and who you would work with, and applications go to the same address. No recruiter in the middle, no form with fourteen fields.
                                </Body>
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={140}>
                        <Lead className="mt-8 max-w-[620px]">
                            Speculative notes are read by the people you would actually work with. A paragraph and a link beat a five-page application every time.
                        </Lead>
                    </Reveal>
                </Container>
            </Section>

            {/* ── Closing CTA ────────────────────────────────────────────── */}
            <CTASection
                eyebrow="Get in touch"
                title="Tell us what you would want to own."
                body="Roles are listed on this page as they open. Until then, a short note about your work and what you would want to build is enough to start."
                primary={{ label: 'Talk to us', to: '/contact' }}
                secondary={{ label: `Email ${CAREERS_EMAIL}`, href: `mailto:${CAREERS_EMAIL}` }}
            />
        </MarketingShell>
    );
}
